/**
 * URL Params Initializer Component
 *
 * Syncs token, transaction_id, API version, and context data from URL parameters to the store
 * once at app initialization. Since these values never change during a transaction, we only
 * need to sync them once. This eliminates the need for duplicate useEffect hooks in every route.
 *
 * API Version Detection:
 * - accesstoken parameter → v5 API (OAuth flow, all params from URL)
 * - token + context params (operation, wallet_provider, asset_code) → v2 API (JWT, context from URL)
 * - token + refNumber → v2 API (JWT, standalone status page pattern)
 * - token without context params or refNumber → v3 API (JWT, context from prepare endpoint)
 *
 * Context Data:
 * - For v2/v5: Context (operation, lang, asset_code, wallet_provider) comes from URL params
 * - For v3: Context comes from PrepareInitializer via /transactions/prepare endpoint
 *
 * Routing:
 * - For v2/v5 with operation=deposit: Redirects to deposit entry page
 */

import { useNavigate, useSearch } from "@tanstack/react-router";
import { useEffect, useRef } from "react";
import { env } from "@/config/env";
import {
	getApiVersionFromSearchParams,
	validateRampsSearch,
} from "../lib/ramps-search-schema";
import { rampsActions, useRampsStore } from "../stores/use-ramps-store";

export function URLParamsInitializer() {
	const search = useSearch({ strict: false });
	const navigate = useNavigate();
	const store = useRampsStore();
	const hasInitialized = useRef(false);

	useEffect(() => {
		// Only sync once at app initialization
		if (hasInitialized.current) return;
		hasInitialized.current = true;

		// Sync auth token from URL to store (both v3 and v5 API)
		const tokenFromUrl = search.accesstoken || search.token;
		if (tokenFromUrl && tokenFromUrl !== store.token) {
			rampsActions.setToken(tokenFromUrl);
		}

		// Detect and set API version based on URL parameters
		// IMPORTANT: Preserve existing API version if already set (e.g., from previous flow)
		// Only detect if store doesn't have a version yet or if we have strong indicators (refNumber, accesstoken)
		const validatedSearch = validateRampsSearch(search);

		// Strong indicators that should override existing version:
		// 1. OAuth token (always v5)
		// 2. refNumber present (but v3 should be preserved even with refNumber)
		const hasOAuthToken = !!validatedSearch.accesstoken;
		const hasRefNumber = !!validatedSearch.refNumber;
		const hasV2ContextParams = !!(
			validatedSearch.operation &&
			validatedSearch.wallet_provider &&
			validatedSearch.asset_code
		);

		let detectedVersion: "v2" | "v3" | "v5";

		// Preserve store version if:
		// - Store has version AND
		// - Same token AND
		// - No OAuth token (OAuth always overrides) AND
		// - Either: no refNumber OR store version is v3
		//   (v3 should be preserved even with refNumber to maintain flow continuity from transaction-status)
		const shouldPreserveStoreVersion =
			store.apiVersion &&
			tokenFromUrl &&
			tokenFromUrl === store.token &&
			!hasOAuthToken &&
			(!hasRefNumber || store.apiVersion === "v3");

		if (shouldPreserveStoreVersion) {
			// Preserve existing version - don't overwrite
			// v3 is preserved even if refNumber is in URL (refNumber might be for API, not v2 indicator)
			detectedVersion = store.apiVersion;
			if (env.isDevelopment) {
				console.log(
					`🔍 Preserving existing API version: ${store.apiVersion} (from previous flow)`,
				);
			}
		} else {
			// Detect version: either store is empty, tokens don't match, or we have strong indicators
			// Check if this is a standalone transaction status page
			const isStandaloneTransactionStatus =
				window.location.pathname.includes("/transaction-status") &&
				!!validatedSearch.token &&
				!validatedSearch.accesstoken &&
				!hasV2ContextParams &&
				!validatedSearch.refNumber;
			const detectedVersionFromUrl = getApiVersionFromSearchParams(
				validatedSearch,
				isStandaloneTransactionStatus,
			);
			// Use detected version if explicit, otherwise fall back to store or default
			detectedVersion = detectedVersionFromUrl ?? store.apiVersion ?? "v3";
			if (detectedVersion !== store.apiVersion) {
				rampsActions.setApiVersion(detectedVersion);
				if (env.isDevelopment) {
					const versionSource =
						detectedVersion === "v5"
							? "accesstoken (OAuth)"
							: detectedVersion === "v2"
								? isStandaloneTransactionStatus
									? "token on standalone transaction status (JWT v2 fallback)"
									: search.refNumber
										? "token + refNumber (JWT v2 standalone)"
										: "token + URL context params (JWT v2)"
								: "token only (JWT v3 - will use prepare endpoint)";
					console.log(
						`🔍 API Version detected: ${detectedVersion} from: ${versionSource}`,
					);
				}
			}
		}

		// Sync transaction ID from URL to store (both v3 and v5)
		// For v3: URL param is initial value, prepare endpoint may override
		// For v5: URL param is the authoritative source
		if (
			search.transaction_id &&
			search.transaction_id !== store.transactionId
		) {
			rampsActions.setTransactionId(search.transaction_id);
		}

		// For v2/v5: Set context data from URL parameters
		// For v3: Context will be set by PrepareInitializer from /transactions/prepare
		if (detectedVersion === "v2" || detectedVersion === "v5") {
			rampsActions.setContextData({
				operation: validatedSearch.operation || "withdraw",
				lang: validatedSearch.lang || "en",
				assetCode: validatedSearch.asset_code || "USDC",
				walletProvider: validatedSearch.wallet_provider || "",
			});
			if (env.isDevelopment) {
				console.log(
					`📋 Context data set from URL params (${detectedVersion}):`,
					{
						operation: validatedSearch.operation,
						lang: validatedSearch.lang,
						assetCode: validatedSearch.asset_code,
						walletProvider: validatedSearch.wallet_provider,
					},
				);
			}

			// For v2/v5 with operation=deposit: Redirect to deposit entry page
			// This mirrors the redirect logic in PrepareInitializer for v3 API
			// IMPORTANT: Never redirect from transaction-status page
			if (
				validatedSearch.operation === "deposit" &&
				!window.location.pathname.includes("/deposit") &&
				!window.location.pathname.includes("/transaction-status")
			) {
				if (env.isDevelopment) {
					console.log(
						"🔀 Redirecting to deposit page based on operation param",
					);
				}
				navigate({
					to: "/ramps/deposit",
					search: {
						...validatedSearch,
						step: "deposit",
					},
				});
			}
		}
	}, [search, store.transactionId, store.token, store.apiVersion, navigate]);

	// This is an initialization component - renders nothing
	return null;
}
